// ADR-111: a photo is shrunk on the phone before it is sent — longest side 800 px, WebP (JPEG where the browser
// cannot write WebP), about 100 KB.
export const PHOTO_MAX_PX = 800;
const QUALITY = 0.8;

/** The size a photo is drawn at: the longest side at most `max`, never enlarged. */
export function fitWithin(width: number, height: number, max = PHOTO_MAX_PX): { width: number; height: number } {
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

export class NotAnImage extends Error {}

export async function shrinkPhoto(file: Blob): Promise<{ blob: Blob; ext: 'webp' | 'jpg' }> {
  if (!file.type.startsWith('image/')) throw new NotAnImage();
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new NotAnImage();
  }
  const { width, height } = fitWithin(bitmap.width, bitmap.height);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new NotAnImage();
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const encode = (type: string) => new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, QUALITY));
  const webp = await encode('image/webp');
  if (webp && webp.type === 'image/webp') return { blob: webp, ext: 'webp' };
  const jpg = await encode('image/jpeg');
  if (!jpg) throw new NotAnImage();
  return { blob: jpg, ext: 'jpg' };
}
